
import { defineComponent, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { EXTERNAL_LINKS } from '@/constants/links';
import TokenSearchInput from '@/components/inputs/TokenSearchInput.vue';
// @ts-ignore
import FarmPoolsTable from '@/components/tables/PoolsTable/FarmPoolsTable.vue';
import MyFarmPoolsTable from '@/components/tables/PoolsTable/MyFarmPoolsTable.vue';
import useFarmPools from '@/composables/farm/useFarmPools';
import useWeb3 from '@/services/web3/useWeb3';
import usePoolFilters from '@/composables/pools/usePoolFilters';
import useAlerts, { AlertPriority, AlertType } from '@/composables/useAlerts';
import { isMigratablePool } from '@/composables/usePool';
import { MIN_FIAT_VALUE_POOL_MIGRATION } from '@/constants/pools';
import { bnum } from '@/lib/utils';
import useUserInfosQuery from '@/composables/queries/useUserInfosQuery';

export default defineComponent({
  components: {
    TokenSearchInput,
    FarmPoolsTable,
    MyFarmPoolsTable
  },

  setup() {
    // COMPOSABLES
    const router = useRouter();
    const { t } = useI18n();
    const { isWalletReady, appNetworkConfig } = useWeb3();
    const isElementSupported = appNetworkConfig.supportsElementPools;
    const {
      selectedTokens,
      addSelectedToken,
      removeSelectedToken
    } = usePoolFilters();

    const {
      pools,
      userPools,
      isLoadingPools,
      isLoadingUserPools,
      loadMorePools,
      poolsHasNextPage,
      poolsIsFetchingNextPage,
      poolsQuery
    } = useFarmPools(selectedTokens);

    const {
      data: userInfos,
      isLoading: isLoadingUserInfos
    } = useUserInfosQuery({});

    const { addAlert, removeAlert } = useAlerts();

    // COMPUTED
    const filteredPools = computed(() =>
      selectedTokens.value.length > 0
        ? pools.value?.filter(pool => {
            return selectedTokens.value.every((selectedToken: string) =>
              pool.tokenAddresses.includes(selectedToken)
            );
          })
        : pools?.value
    );

    const showMigrationColumn = computed(() =>
      userPools.value?.some(pool => {
        return (
          isMigratablePool(pool) &&
          // TODO: this is a temporary solution to allow only big holders to migrate due to gas costs.
          bnum(pool.shares).gt(MIN_FIAT_VALUE_POOL_MIGRATION)
        );
      })
    );

    function navigateToStakeFlake() {
      router.push({ name: 'stake-flake' });
    }

    // userPools.value[0].shares
    watch(poolsQuery.error, () => {
      if (poolsQuery.error.value) {
        addAlert({
          id: 'pools-fetch-error',
          label: t('alerts.pools-fetch-error'),
          type: AlertType.ERROR,
          persistent: true,
          action: poolsQuery.refetch.value,
          actionLabel: t('alerts.retry-label'),
          priority: AlertPriority.MEDIUM
        });
      } else {
        removeAlert('pools-fetch-error');
      }
    });

    watch(showMigrationColumn, () => console.log(showMigrationColumn.value));

    return {
      // data
      pools,
      filteredPools,
      userPools,
      userInfos,
      isLoadingPools,
      isLoadingUserInfos,
      isLoadingUserPools,

      // computed
      isWalletReady,
      poolsHasNextPage,
      poolsIsFetchingNextPage,
      selectedTokens,
      isElementSupported,
      showMigrationColumn,

      //methods
      router,
      loadMorePools,
      addSelectedToken,
      removeSelectedToken,
      navigateToStakeFlake,
      // constants
      EXTERNAL_LINKS
    };
  }
});
